import { Injectable } from '@angular/core';
import { MarkdownService } from '../markdown.service';
import { POI } from '../../models/data-layer-type';
import { validateKeyValue } from '../../validators/district-key.validator';
import {
  ImportPoiValidationHints,
  ImportPoiValidationResult,
} from '../../models/import-data';

@Injectable({
  providedIn: 'root',
})
export class PoiImportValidatorService {
  public errors: Record<number, Record<string, boolean>> | false = false;
  public warnings: Record<number, Record<string, boolean>> = {};
  public hints: ImportPoiValidationHints = {};
  public isInvalid = false;
  public convertAvailable = false;

  private propertiesToConvert: Array<keyof POI> = [
    'description',
    'contact',
    'information',
  ];

  constructor(private markdownService: MarkdownService) {}

  /**
   * Validates a list of POIs (Points of Interest) and maps their properties according to specified rules.
   *
   * @param {POI[]} poiList - An array of POI objects to validate and map. Each POI object may include properties such as `key`, `name`, `geoPositionX`, and `geoPositionY`.
   * @param {string[]} existingKeys - An array of keys that are already in use, used to check for duplicates.
   * @return {ImportPoiValidationResult} A validation result object containing the processed list of POIs, validation errors, warnings, and hints. The structure includes:
   * - `poi`: The updated list of POIs with validation statuses applied.
   * - `errors`: An object detailing validation errors identified in the POIs.
   * - `warnings`: An object detailing warnings encountered during validation.
   * - `hints`: An object providing hints or suggestions based on validation results.
   */
  validateAndMap(
    poiList: POI[],
    existingKeys: string[]
  ): ImportPoiValidationResult {
    this.errors = {};
    this.hints = {};
    this.warnings = {};

    if (poiList.length > 0) {
      const keyList: string[] = [];
      poiList.forEach((poi, index) => {
        poi = this.convertFromMarkdown(poi);
        // Validate districtKey using validateKeyValue
        let districtKeyErrors = validateKeyValue(poi.key ?? '');

        // duplicates
        if (keyList.includes(poi.key)) {
          districtKeyErrors
            ? (districtKeyErrors['duplicate'] = true)
            : (districtKeyErrors = { duplicate: true });
        }

        if (districtKeyErrors) {
          this.setError(index, 'districtKey');
          Object.keys(districtKeyErrors).forEach(errorKey => {
            this.setError(index, errorKey);
          });
          poi.validation = 'error';
        } else {
          poi.validation = existingKeys.includes(poi.key) ? 'info' : 'success';
          keyList.push(poi.key);
        }

        if (!poi.name) {
          this.setError(index, 'name');
          poi.validation = 'error';
        }

        poi.geoPositionX =
          poi.geoPositionX && !isNaN(Number(poi.geoPositionX))
            ? Number(poi.geoPositionX)
            : undefined;
        poi.geoPositionY =
          poi.geoPositionY && !isNaN(Number(poi.geoPositionY))
            ? Number(poi.geoPositionY)
            : undefined;

        if (!this.validateCoordinates(poi.geoPositionY, poi.geoPositionX)) {
          this.setWarning(index, 'geoPositionX', 'geoPositionY');
          this.convertAvailable = true;
          if (poi.validation !== 'error') poi.validation = 'warning';
        } else {
          this.clearWarning(index, 'geoPositionX', 'geoPositionY');
        }
      });
    }

    this.isInvalid = this.errors && Object.keys(this.errors).length > 0;
    this.errors = Object.keys(this.errors).length > 0 ? this.errors : false;

    return {
      poi: poiList,
      errors: this.errors,
      warnings: this.warnings,
      hints: this.hints,
    };
  }

  /**
   * Validates the given coordinates by checking if the latitude and longitude
   * are within valid ranges and satisfy certain conditions.
   *
   * @param y The latitude value to validate.
   * @param x The longitude value to validate.
   * @return Returns true if the coordinates are valid; otherwise, false.
   */
  private validateCoordinates(y?: number, x?: number): boolean {
    if (!y || !x) return false;

    const yIsValid = this.isValidLatitude(y);
    const xIsValid = this.isValidLongitude(x);
    let valid = true;

    if (!yIsValid || !xIsValid) {
      valid = false;
    }

    if (y < x) {
      valid = false;
    }
    if (!valid) {
      this.hints.invalidGeoData = true;
    }
    return valid;
  }

  /**
   * Checks if the value is a valid latitude.
   * @param latitude Latitude value to check
   * @returns True if valid, false otherwise
   */
  private isValidLatitude(latitude: number): boolean {
    const valid = latitude >= -90 && latitude <= 90;
    if (!valid) {
      this.hints.invalidGeoData = true;
    }
    return valid;
  }

  /**
   * Checks if the value is a valid longitude.
   * @param longitude Longitude value to check
   * @returns True if valid, false otherwise
   */
  private isValidLongitude(longitude: number): boolean {
    const valid = longitude >= -180 && longitude <= 180;
    if (!valid) {
      this.hints.invalidGeoData = true;
    }
    return valid;
  }

  /**
   * Sets an error for a specific index and key in the errors object.
   *
   * @param {number} index - The index at which the error will be set.
   * @param {string} key - The key corresponding to the specific error at the given index.
   * @return {void} Does not return a value.
   */
  private setError(index: number, key: string): void {
    if (!this.errors) this.errors = {};
    if (!this.errors[index]) this.errors[index] = {};
    this.errors[index][key] = true;
  }

  /**
   * Updates the warning status for a specific index by enabling multiple warning keys.
   *
   * @param {number} index - The index representing the target location in the warnings collection.
   * @param {...string} keys - The keys associated with specific warnings to be set for the given index.
   * @return {void} This method does not return a value.
   */
  private setWarning(index: number, ...keys: string[]): void {
    if (!this.warnings[index]) this.warnings[index] = {};
    keys.forEach(key => {
      this.warnings[index][key] = true;
    });
  }

  /**
   * Clears specified warning keys at the provided index from the warnings collection.
   *
   * @param {number} index - The index of the warning entry to modify.
   * @param {...string} keys - The keys of the warnings to be cleared from the specified index.
   * @return {void} This function does not return any value.
   */
  private clearWarning(index: number, ...keys: string[]): void {
    if (this.warnings[index]) {
      keys.forEach(key => {
        delete this.warnings[index][key];
      });
    }
  }

  /**
   * Converts specific properties of a POI object from Markdown format to HTML.
   *
   * @param {POI} poi The Point of Interest (POI) object containing properties in Markdown format.
   * @return {POI} The updated POI object with converted HTML content in the specified properties.
   */
  private convertFromMarkdown(poi: POI) {
    for (const property of this.propertiesToConvert) {
      const value = poi[property];
      if (typeof value === 'string') {
        poi[property] = this.markdownService.convertToHtml(value);
      } else if (typeof value === 'object' && value !== null) {
        if (
          'hyperlink' in value &&
          typeof value.hyperlink === 'string' &&
          value.hyperlink.startsWith('http')
        ) {
          poi[property] =
            `<a href="${value.hyperlink}" target="_blank">${this.markdownService.convertToHtml(value.text)}</a>`;
        }
      } else {
        poi[property] = ''; // Assign a default value if not a string
      }
    }

    return poi;
  }
}
